/*
 * What the public developer page says about each address — DESIGN-api-and-webhooks.md
 * (Rob 25/10/2026: layout A, examples in curl AND PowerShell).
 *
 * ⚠️ **THE LIST OF ADDRESSES MUST MATCH THE API.** `backend/tests/test_developer_page.py`
 * reads every `path:` below and every route on `routers/public_api.router`, and fails if
 * either has one the other hasn't — a page describing an address that doesn't exist, or an
 * address nobody told developers about.
 *
 * ⚠️ **EVERY ADDRESS SAYS ITS METHOD** (`method: 'GET'`, `'POST'`, `'PATCH'` or `'DELETE'`,
 * right before `path:`). Since step 3 (26/10/2026) `/v1/bookings` is read AND written, so the
 * test compares (method, path) pairs, not paths. Step 4 (26/10/2026) added PATCH (change only
 * what's sent) and DELETE (an empty department); step 5 (26/10/2026) added PUT (set a person's
 * allowance to the number sent).
 *
 * ⚠️ **EXAMPLES ARE MADE UP AND MUST NEVER CONTAIN A REASON.** The same test fails if a
 * `reason` appears anywhere outside a `fields: [...]` list — the description of what a write
 * TAKES (Rob's D A: a reason may come in). It never goes out, so no reply or webhook example
 * may show one: a developer copies examples, and an example field is a promise.
 *
 * Pure data and two pure helpers — `src/lib/apiReference.checks.mjs` runs them.
 */

export const SECTIONS = [
  { id: 'start', label: 'Start here', items: [
    { id: 'getting-a-key', label: 'Getting a key' },
    { id: 'first-request', label: 'Your first request' },
    { id: 'never-sent', label: 'What we never send' },
  ] },
  { id: 'rules', label: 'The rules', items: [
    { id: 'limits', label: 'Limits' },
    { id: 'dates', label: 'Dates and amounts' },
    { id: 'errors', label: 'Errors' },
    { id: 'pages', label: 'Pages of results' },
  ] },
  { id: 'reference', label: 'Reading', items: [] },   // filled from ENDPOINTS below (GET)
  { id: 'changing', label: 'Changing leave', items: [   // then the leave changes below
    { id: 'changing-leave', label: 'How changing leave works' },
    { id: 'same-request', label: 'Retrying safely' },
  ] },
  { id: 'managing', label: 'Managing people', items: [   // then the people changes below
    { id: 'managing-people', label: 'How managing people works' },
  ] },
  { id: 'allowances', label: 'Allowances and leave types', items: [   // then step 5's changes
    { id: 'managing-allowances', label: 'How managing allowances works' },
  ] },
  { id: 'hooks', label: 'Webhooks', items: [
    { id: 'webhooks', label: 'How webhooks work' },
    { id: 'webhook-events', label: 'The events' },
    { id: 'webhook-message', label: 'What a message looks like' },
    { id: 'webhook-signature', label: 'Checking it came from us' },
    { id: 'webhook-retries', label: 'Retries and duplicates' },
  ] },
];

/* The words for each tick, as Settings → Integrations shows them. */
export const TICKS = {
  any: 'Any key',
  people: 'People, departments and locations',
  leave: "Leave and who's off",
  balances: 'Balances',
  book: 'Book and approve leave',
  manage_people: 'Manage people and departments',
  manage_allowances: 'Manage allowances and leave types',
};

const JO = { id: '6d7a1b71-9beb-4f5b-994a-19467fbbc927', name: 'Jo Bloggs' };
const SAM = { id: 'a3f0c2de-4b1e-4c55-9e0a-2f7b7d1c8a10', name: 'Sam Patel' };
const ANNUAL = { id: '3d18d31d-383f-48db-8d2d-01ac66d7a695', name: 'Annual Leave', colour: '#F59E0B' };
const TIME_OFF = { id: 'time_off', name: 'Time off', colour: '#94a3b8' };
/* In a booking, the person carries their work email too — but on the API only when the key can
 * also read people (otherwise it's `null`). A webhook always has it. (Rob's 2A, 25/10/2026.) */
const JO_IN_BOOKING = { ...JO, email: 'jo.bloggs@acme.example' };

export const ENDPOINTS = [
  {
    id: 'key', method: 'GET', path: '/v1/key', tick: 'any', title: 'This key',
    what: 'Which key you’re using, which company it’s for, and what it can read and change. The first thing to try with a new key.',
    params: [],
    example: '/v1/key',
    reply: { name: 'Hive feed', company: 'Acme Ltd', can_read: ['people', 'leave'], shows_sickness_as_sickness: false, can_book_and_approve: false, can_manage_people: false, can_manage_allowances: false },
  },
  {
    id: 'people', method: 'GET', path: '/v1/people', tick: 'people', title: 'People',
    what: 'Everyone in the company, A to Z by surname. People who have left are only included if you ask.',
    params: [
      { name: 'include_leavers', says: '`true` to include people who have left.' },
      { name: 'limit', says: 'How many to a page, 1 to 500. Normally 100.' },
      { name: 'after', says: 'The `next` value from the previous page.' },
    ],
    example: '/v1/people?limit=2',
    reply: {
      data: [{
        id: JO.id, first_name: 'Jo', last_name: 'Bloggs', name: 'Jo Bloggs', email: 'jo@acme.co.uk',
        job_title: 'Account manager', department: { id: 'c1d2…', name: 'Sales' },
        location: { id: 'e5f6…', name: 'Leeds' }, reports_to_id: 'b7c8…',
        started_on: '2021-03-01', leaves_on: null, has_left: false, unit: 'days',
      }],
      next: 'bzoy',
    },
  },
  {
    id: 'person', method: 'GET', path: '/v1/people/{person_id}', tick: 'people', title: 'One person',
    what: 'One person, by their id. The same fields as in the list.',
    params: [],
    example: `/v1/people/${JO.id}`,
    reply: { id: JO.id, name: 'Jo Bloggs', email: 'jo@acme.co.uk', '…': '…' },
  },
  {
    id: 'departments', method: 'GET', path: '/v1/departments', tick: 'people', title: 'Departments',
    what: 'The company’s departments, A to Z, with each one’s manager and how many may be off at once (`max_off`, 0 for no limit).',
    params: [],
    example: '/v1/departments',
    reply: { data: [{ id: 'c1d2…', name: 'Sales', manager_id: 'b7c8…', max_off: 2 }, { id: 'd3e4…', name: 'Support', manager_id: null, max_off: 0 }] },
  },
  {
    id: 'locations', method: 'GET', path: '/v1/locations', tick: 'people', title: 'Locations',
    what: 'The company’s offices, with their country and time zone.',
    params: [],
    example: '/v1/locations',
    reply: { data: [{ id: 'e5f6…', name: 'Leeds', country_code: 'GB', timezone: 'Europe/London', is_head_office: true }] },
  },
  {
    id: 'leave-types', method: 'GET', path: '/v1/leave-types', tick: 'leave', title: 'Leave types',
    what: 'The company’s leave types, with their settings. Private types, and sickness unless the key shows it, are one “Time off” entry with no settings.',
    params: [],
    example: '/v1/leave-types',
    reply: { data: [
      { ...ANNUAL, icon: 'palmtree', counts_against_allowance: true, needs_approval: true, approve_automatically: false,
        counts_towards_max_off: true, yearly_limit_days: null, is_annual_leave: true, is_time_in_lieu: false },
      { ...TIME_OFF, counts_against_allowance: null, needs_approval: null },
    ] },
  },
  {
    id: 'leave-type-colours', method: 'GET', path: '/v1/leave-types/colours', tick: 'leave', title: 'Leave type colours',
    what: 'The colours a leave type can have: the same choices as Settings → Leave types. `in_use` says another type already has it (a type needs a colour of its own). Purple is kept for bank holidays.',
    params: [],
    example: '/v1/leave-types/colours',
    reply: { data: [{ value: '#f59e0b', name: 'Amber', in_use: true }, { value: '#3b82f6', name: 'Blue', in_use: false }, { '…': '…' }] },
  },
  {
    id: 'leave-type-icons', method: 'GET', path: '/v1/leave-types/icons', tick: 'leave', title: 'Leave type icons',
    what: 'The icons a leave type can have: the same choices as Settings → Leave types.',
    params: [],
    example: '/v1/leave-types/icons',
    reply: { data: [{ value: 'palmtree', name: 'Palm Tree' }, { value: 'plane', name: 'Plane' }, { '…': '…' }] },
  },
  {
    id: 'bookings', method: 'GET', path: '/v1/bookings', tick: 'leave', title: 'Bookings',
    what: 'Approved leave that touches the dates you give, earliest first. Requests still waiting for approval aren’t included, unless the key can book and approve leave and asks with `status`. The person’s `email` is filled in only if the key can also read people; otherwise it’s `null`.',
    params: [
      { name: 'from', required: true, says: 'First date, like 2026-11-02.' },
      { name: 'to', required: true, says: 'Last date. At most 400 days after `from`.' },
      { name: 'person_id', says: 'Just this person.' },
      { name: 'department_id', says: 'Just this department.' },
      { name: 'status', says: 'Normally `approved`. A key that can book and approve leave may ask for `requested`, `declined` or `cancelled` too, separated by commas.' },
      { name: 'limit', says: 'How many to a page, 1 to 500. Normally 100.' },
      { name: 'after', says: 'The `next` value from the previous page.' },
    ],
    example: '/v1/bookings?from=2026-11-01&to=2026-11-30',
    reply: { data: [{
      id: '7e8c4d0d-1dac-4826-a740-87cf92d3cd89', person: JO_IN_BOOKING, leave_type: ANNUAL,
      starts: '2026-11-02', ends: '2026-11-06', start_part: 'full_day', end_part: 'full_day',
      start_time: null, end_time: null, amount: { unit: 'days', value: 5, text: '5 days' },
      status: 'approved', booked_at: '2026-10-20T09:14:02Z', approved_at: '2026-10-20T11:30:45Z',
    }], next: null },
  },
  {
    id: 'booking', method: 'GET', path: '/v1/bookings/{booking_id}', tick: 'leave', title: 'One booking',
    what: 'One approved booking, by its id. A key that can book and approve leave gets it whatever its status.',
    params: [],
    example: '/v1/bookings/7e8c4d0d-1dac-4826-a740-87cf92d3cd89',
    reply: { id: '7e8c4d0d-1dac-4826-a740-87cf92d3cd89', person: JO, '…': '…' },
  },
  {
    id: 'whos-off', method: 'GET', path: '/v1/whos-off', tick: 'leave', title: 'Who’s off',
    what: 'Day by day, who’s off: approved leave on the days each person normally works. Every day is listed, even when nobody is off.',
    params: [
      { name: 'from', says: 'First date. Normally today.' },
      { name: 'to', says: 'Last date. Normally the same as `from`. At most 62 days.' },
      { name: 'department_id', says: 'Just this department.' },
    ],
    example: '/v1/whos-off?from=2026-11-02&to=2026-11-03',
    reply: { data: [
      { date: '2026-11-02', off: [
        { person: JO, leave_type: ANNUAL, part: 'full_day', booking_id: '7e8c…' },
        { person: SAM, leave_type: TIME_OFF, part: 'afternoon', booking_id: '9a1b…' },
      ] },
      { date: '2026-11-03', off: [{ person: JO, leave_type: ANNUAL, part: 'full_day', booking_id: '7e8c…' }] },
    ] },
  },
  {
    id: 'balances', method: 'GET', path: '/v1/balances', tick: 'balances', title: 'Balances',
    what: 'Annual leave this leave year, for each person: the same numbers as Reports → What we owe. `time_in_lieu_owed_hours` is their time in lieu still to take (`null` if they’ve never had any).',
    params: [{ name: 'person_id', says: 'Just this person.' }],
    example: '/v1/balances',
    reply: { data: [{
      person: JO, leave_year: { start: '2026-01-01', end: '2026-12-31' }, unit: 'days', has_allowance: true,
      allowance: { days: 25, hours: 187.5 }, taken: { days: 12, hours: 90 },
      booked_ahead: { days: 5, hours: 37.5 }, waiting_for_approval: { days: 0, hours: 0 },
      left: { days: 8, hours: 60 }, time_in_lieu_owed_hours: null,
    }] },
  },
  {
    id: 'time-in-lieu', method: 'GET', path: '/v1/time-in-lieu', tick: 'balances', title: 'Time in lieu',
    what: 'Time in lieu claims, corrections and expiries, newest first: what each person’s time in lieu is worked out from. `minutes` is what the entry does to their time in lieu (a minus takes away; `null` while a claim is waiting). What anybody wrote with an entry is never sent.',
    params: [
      { name: 'person_id', says: 'Just this person.' },
      { name: 'status', says: '`waiting`, `approved`, `declined` or `withdrawn`.' },
      { name: 'kind', says: '`claim`, `correction` or `expiry`.' },
      { name: 'after, limit', says: 'Pages, as everywhere.' },
    ],
    example: '/v1/time-in-lieu?status=waiting',
    reply: { data: [{
      id: '5c6d7e8f-9a0b-4c1d-8e2f-3a4b5c6d7e8f', person: SAM, kind: 'claim', status: 'waiting',
      worked_on: '2026-10-24', applies_from: '2026-10-24', worked_minutes: 180, rate: null, minutes: null,
      use_by: null, recorded_by: SAM, decided_by: null, decided_at: null, created_at: '2026-10-24T17:40:02Z',
    }], next: null },
  },

  /* ── Changing leave (step 3, Rob 26/10/2026) ─────────────────────────────────────────
   * Every one is done AS THE ADMIN WHO MADE THE KEY, through the app's own booking code.
   * `fields` is what the write takes — the only place a reason may be mentioned (it comes in,
   * never goes out). `body` is the made-up request; `reply` what comes back. */
  {
    id: 'book', method: 'POST', path: '/v1/bookings', tick: 'book', title: 'Book leave',
    what: 'Book leave for one person, as the admin who made the key. It’s approved straight away, as an admin’s booking is in the app, unless you send `send_for_approval`. Answers `201` with the booking and a sentence saying what happened.',
    params: [],
    fields: [
      { name: 'person_id', required: true, says: 'Who it’s for, from `/v1/people`.' },
      { name: 'leave_type_id', required: true, says: 'From `/v1/leave-types`. A type this key sees as `Time off` (a private type, or sickness without “Show sickness as sickness”) can’t be booked through the API.' },
      { name: 'starts', required: true, says: 'The first day, like 2026-11-02.' },
      { name: 'ends', says: 'The last day. Normally the same as `starts`.' },
      { name: 'start_part', says: '`full_day` (normal), or `afternoon` to start at lunchtime.' },
      { name: 'end_part', says: '`full_day` (normal), or `morning` to finish at lunchtime.' },
      { name: 'start_time', says: 'With `end_time`, for leave booked by the hour: `09:00`. One day only, and only where the company books in hours.' },
      { name: 'end_time', says: 'Like `11:00`.' },
      { name: 'reason', says: 'Optional. Kept, like the box in the app. It is never sent back out.' },
      { name: 'send_for_approval', says: '`true` to send it to the person’s approver as if they had asked themselves. Normally `false`.' },
    ],
    example: '/v1/bookings',
    body: { person_id: JO.id, leave_type_id: ANNUAL.id, starts: '2026-11-02', ends: '2026-11-06' },
    reply: {
      booking: {
        id: '7e8c4d0d-1dac-4826-a740-87cf92d3cd89', person: { ...JO, email: null }, leave_type: ANNUAL,
        starts: '2026-11-02', ends: '2026-11-06', start_part: 'full_day', end_part: 'full_day',
        start_time: null, end_time: null, amount: { unit: 'days', value: 5, text: '5 days' },
        status: 'approved', booked_at: '2026-10-26T09:14:02Z', approved_at: null,
      },
      message: 'Booked and approved: Jo Bloggs, 02/11/2026 to 06/11/2026.',
    },
  },
  {
    id: 'approve', method: 'POST', path: '/v1/bookings/{booking_id}/approve', tick: 'book', title: 'Approve a request',
    what: 'Approve a request that’s waiting, as the admin who made the key. Where a request needs more than one approval, this signs the step it’s waiting on. A request that’s already been dealt with answers `409` with `already_decided`.',
    params: [],
    example: '/v1/bookings/9a1b2c3d-4e5f-4a6b-8c7d-0e1f2a3b4c5d/approve',
    reply: { booking: { id: '9a1b2c3d-4e5f-4a6b-8c7d-0e1f2a3b4c5d', person: SAM, status: 'approved', '…': '…' },
      message: 'Approved: Sam Patel, 16/11/2026 to 17/11/2026.' },
  },
  {
    id: 'decline', method: 'POST', path: '/v1/bookings/{booking_id}/decline', tick: 'book', title: 'Decline a request',
    what: 'Decline a request that’s waiting, as the admin who made the key.',
    params: [],
    fields: [{ name: 'note', says: 'Optional. Kept as the decline note, which the person sees in the app. It is never sent back out.' }],
    example: '/v1/bookings/9a1b2c3d-4e5f-4a6b-8c7d-0e1f2a3b4c5d/decline',
    reply: { booking: { id: '9a1b2c3d-4e5f-4a6b-8c7d-0e1f2a3b4c5d', person: SAM, status: 'declined', '…': '…' },
      message: 'Declined: Sam Patel, 16/11/2026 to 17/11/2026.' },
  },
  {
    id: 'cancel', method: 'POST', path: '/v1/bookings/{booking_id}/cancel', tick: 'book', title: 'Cancel a booking',
    what: 'Cancel the whole of a booking, approved or still waiting, as the admin who made the key. Its days go back on the person’s balance.',
    params: [],
    example: '/v1/bookings/7e8c4d0d-1dac-4826-a740-87cf92d3cd89/cancel',
    reply: { booking: { id: '7e8c4d0d-1dac-4826-a740-87cf92d3cd89', person: JO, status: 'cancelled', '…': '…' },
      message: 'Cancelled: Jo Bloggs, 02/11/2026 to 06/11/2026.' },
  },
  {
    id: 'give-back', method: 'POST', path: '/v1/bookings/{booking_id}/give-back', tick: 'book', title: 'Give back some days',
    what: 'Give back some of a booking’s days. Giving back a day in the middle splits it in two: `booking` is the first part and `other_parts` the rest. Giving back every day cancels it.',
    params: [],
    fields: [{ name: 'days', required: true, says: 'The dates to give back, like `["2026-11-04"]`.' }],
    example: '/v1/bookings/7e8c4d0d-1dac-4826-a740-87cf92d3cd89/give-back',
    body: { days: ['2026-11-04'] },
    reply: {
      booking: { id: '7e8c4d0d-1dac-4826-a740-87cf92d3cd89', starts: '2026-11-02', ends: '2026-11-03', status: 'approved', '…': '…' },
      other_parts: [{ id: 'c4d5e6f7-0a1b-4c2d-9e8f-7a6b5c4d3e2f', starts: '2026-11-05', ends: '2026-11-06', status: 'approved', '…': '…' }],
      given_back: ['2026-11-04'],
      message: 'Gave back 1 day of Jo Bloggs’s leave: Wed 04/11/2026.',
    },
  },
  /* ── Managing people and departments (step 4, Rob 26/10/2026) ─────────────────────────
   * As the admin who made the key, through the app's own code. Never an admin, never a role,
   * never a person deleted. Personal details (date of birth, payroll ref, phones, address,
   * emergency contacts) go IN and are never sent back out — no reply below shows them. */
  {
    id: 'add-person', method: 'POST', path: '/v1/people', tick: 'manage_people', title: 'Add a person',
    what: 'Add somebody, as the admin who made the key. They’re always an employee, and they choose their own password when they first sign in. Answers `201` with the person, how many people the company is now billed for, and a sentence saying what happened.',
    params: [],
    fields: [
      { name: 'first_name', required: true, says: 'Their first name.' },
      { name: 'last_name', required: true, says: 'Their surname.' },
      { name: 'email', says: 'Their work email, which they sign in with. Optional: somebody without one can have leave but can’t sign in.' },
      { name: 'send_invite', says: '`true` to email them an invitation to sign in. Normally `false`.' },
      { name: 'job_title', says: 'Words only; it never decides what anybody can do.' },
      { name: 'department_id', says: 'From `/v1/departments`.' },
      { name: 'location_id', says: 'From `/v1/locations`.' },
      { name: 'reports_to_id', says: 'Their manager, from `/v1/people`. They also approve their leave unless the app says otherwise.' },
      { name: 'started_on', says: 'Their first day, like 2026-11-02.' },
      { name: 'unit', says: '`days` or `hours`: how their leave is counted.' },
      { name: 'date_of_birth, payroll_id, mobile_phone, home_phone', says: 'Optional. Kept in the app and never sent back out.' },
      { name: 'address_line_1, address_line_2, address_line_3, town, county, postcode, country', says: 'Optional home address. Kept in the app and never sent back out.' },
      { name: 'emergency_contacts', says: 'Optional, up to 5: `[{"name": "Alex Patel", "relation": "Partner", "phone": "01632 960000"}]` (`mobile` too). Never sent back out.' },
    ],
    example: '/v1/people',
    body: { first_name: 'Sam', last_name: 'Patel', email: 'sam.patel@acme.example', department_id: 'c1d2e3f4-a5b6-4c7d-8e9f-0a1b2c3d4e5f', started_on: '2026-11-02' },
    reply: {
      person: { id: SAM.id, first_name: 'Sam', last_name: 'Patel', name: 'Sam Patel', email: 'sam.patel@acme.example', job_title: null,
        department: { id: 'c1d2…', name: 'Sales' }, location: null, reports_to_id: null,
        started_on: '2026-11-02', leaves_on: null, has_left: false, unit: 'days' },
      billed_people: 35,
      message: 'Added Sam Patel. You’re now billed for 35 people.',
    },
  },
  {
    id: 'edit-person', method: 'PATCH', path: '/v1/people/{person_id}', tick: 'manage_people', title: 'Change a person',
    what: 'Change only the fields you send — the same fields as adding, except `send_invite`. `null` clears a job title, department, location or reports-to, and blanks a personal detail; a name, start date, unit, email or date of birth can’t be cleared. An admin can’t be changed by a key. If the email changes, they’re signed out everywhere and their old address is told.',
    params: [],
    example: `/v1/people/${SAM.id}`,
    body: { job_title: 'Senior analyst', department_id: 'd3e4f5a6-b7c8-4d9e-8f0a-1b2c3d4e5f6a' },
    reply: { person: { id: SAM.id, name: 'Sam Patel', job_title: 'Senior analyst', '…': '…' },
      message: 'Updated Sam Patel: job title, department.' },
  },
  {
    id: 'archive-person', method: 'POST', path: '/v1/people/{person_id}/archive', tick: 'manage_people', title: 'Archive a person',
    what: 'Somebody is leaving. Exactly what the app’s Archive does: bookings after their last day are cancelled, their allowance is settled to the time they worked, their approvals can go to somebody else, and they’re signed out. A future `leaves_on` schedules it: they can sign in until then. An admin can’t be archived by a key, and nobody can be deleted.',
    params: [],
    fields: [
      { name: 'leaves_on', says: 'Their last day. Normally today.' },
      { name: 'cancel_later_bookings', says: 'Normally `true`.' },
      { name: 'settle_allowance', says: 'Normally `true`: their allowance is cut to the time they worked.' },
      { name: 'successor_id', says: 'Who takes over the requests waiting for them, from `/v1/people`.' },
      { name: 'move_reporting_lines', says: 'Normally `true`: their team reports to the successor.' },
    ],
    example: `/v1/people/${SAM.id}/archive`,
    body: { leaves_on: '2026-11-30' },
    reply: { person: { id: SAM.id, name: 'Sam Patel', leaves_on: '2026-11-30', has_left: false, '…': '…' },
      archived: false, scheduled: true, leaves_on: '2026-11-30', bookings_cancelled: 1,
      message: 'Sam Patel leaves on 30/11/2026 and will be archived the day after. Until then they can still sign in. 1 later booking was cancelled.' },
  },
  {
    id: 'restore-person', method: 'POST', path: '/v1/people/{person_id}/restore', tick: 'manage_people', title: 'Restore a person',
    what: 'Undo an archive: they can sign in again. For somebody who was going to leave, it clears their leaving date instead (they’re staying). Bookings that were cancelled stay cancelled.',
    params: [],
    example: `/v1/people/${SAM.id}/restore`,
    reply: { person: { id: SAM.id, name: 'Sam Patel', leaves_on: null, has_left: false, '…': '…' },
      message: 'Sam Patel is staying: their leaving date is cleared and their allowance put back.' },
  },
  {
    id: 'add-department', method: 'POST', path: '/v1/departments', tick: 'manage_people', title: 'Add a department',
    what: 'Add a department. Its manager must be one of your people with an email address.',
    params: [],
    fields: [
      { name: 'name', required: true, says: 'Like `Support`.' },
      { name: 'manager_id', says: 'From `/v1/people`.' },
      { name: 'max_off', says: 'How many may be off at once. `0` (normal) for no limit.' },
    ],
    example: '/v1/departments',
    body: { name: 'Support', manager_id: JO.id, max_off: 2 },
    reply: { department: { id: 'd3e4f5a6-b7c8-4d9e-8f0a-1b2c3d4e5f6a', name: 'Support', manager_id: JO.id, max_off: 2 },
      message: 'Added the department Support.' },
  },
  {
    id: 'edit-department', method: 'PATCH', path: '/v1/departments/{department_id}', tick: 'manage_people', title: 'Change a department',
    what: 'Change only what you send: `name`, `manager_id` (`null` takes the manager off) or `max_off`. Anything else about it, like extra managers, stays as it is.',
    params: [],
    fields: [{ name: 'name, manager_id, max_off', says: 'Any of them.' }],
    example: '/v1/departments/d3e4f5a6-b7c8-4d9e-8f0a-1b2c3d4e5f6a',
    body: { name: 'Customer support' },
    reply: { department: { id: 'd3e4f5a6-b7c8-4d9e-8f0a-1b2c3d4e5f6a', name: 'Customer support', manager_id: JO.id, max_off: 2 },
      message: 'Updated the department Customer support: name.' },
  },
  {
    id: 'delete-department', method: 'DELETE', path: '/v1/departments/{department_id}', tick: 'manage_people', title: 'Delete a department',
    what: 'Only when nobody current is in it — move them first. Otherwise you get a `400` with the app’s own sentence.',
    params: [],
    example: '/v1/departments/d3e4f5a6-b7c8-4d9e-8f0a-1b2c3d4e5f6a',
    reply: { deleted: true, id: 'd3e4f5a6-b7c8-4d9e-8f0a-1b2c3d4e5f6a', message: 'Deleted the department Customer support.' },
  },
  /* ── Allowances, time in lieu and leave types (step 5, Rob 26/10/2026) ─────────────────
   * As the admin who made the key, through the app's own code. Never an admin, never a
   * private or sickness type, never Annual Leave's name, never a deletion, never a balance set.
   * A time in lieu note or reason goes IN (`fields`) and is never sent back out. */
  {
    id: 'set-allowance', method: 'PUT', path: '/v1/people/{person_id}/allowance', tick: 'manage_allowances', title: 'Set a person’s allowance',
    what: 'Their yearly allowance in days, exactly like “Annual allowance (days)” on their record in the app (in days even for people whose leave is counted in hours). This leave year’s balance is worked out again, as it is when you save their record. Not for an admin, or somebody archived.',
    params: [],
    fields: [{ name: 'allowance_days', required: true, says: 'A number of days, like `25` or `22.5`. `null` puts them back on the company’s allowance.' }],
    example: `/v1/people/${SAM.id}/allowance`,
    body: { allowance_days: 25 },
    reply: { person: SAM, allowance_days: 25, uses_company_allowance: false, company_allowance_days: 28,
      message: 'Sam Patel’s yearly allowance is now 25 days. This leave year’s balance has been worked out again, as it is when you save their record.' },
  },
  {
    id: 'record-time-in-lieu', method: 'POST', path: '/v1/time-in-lieu', tick: 'manage_allowances', title: 'Record time in lieu',
    what: 'Extra time somebody worked, approved straight away: the same as recording it on their page in the app. They’re told. Time in lieu must be switched on for the company.',
    params: [],
    fields: [
      { name: 'person_id', required: true, says: 'From `/v1/people`. Not an admin.' },
      { name: 'worked_on', required: true, says: 'The day they worked it: today or earlier.' },
      { name: 'worked_minutes', required: true, says: 'How long, in minutes: up to 1440 (24 hours).' },
      { name: 'rate', says: '`1`, `1.5` or `2`. Leave it out for the company’s usual rate.' },
      { name: 'given_minutes', says: 'Instead of a rate: the exact time to give back, in minutes.' },
      { name: 'note', says: 'Optional. Kept in their history and never sent back out.' },
    ],
    example: '/v1/time-in-lieu',
    body: { person_id: SAM.id, worked_on: '2026-10-24', worked_minutes: 180, rate: 1.5 },
    reply: { entry: { id: '5c6d7e8f-9a0b-4c1d-8e2f-3a4b5c6d7e8f', person: SAM, kind: 'claim', status: 'approved', worked_minutes: 180, rate: 1.5, minutes: 270, '…': '…' },
      message: 'Recorded 3h worked by Sam Patel on 24/10/2026: 4h 30m of time in lieu, approved. They’ve been told.' },
  },
  {
    id: 'approve-time-in-lieu', method: 'POST', path: '/v1/time-in-lieu/{entry_id}/approve', tick: 'manage_allowances', title: 'Approve a time in lieu claim',
    what: 'A claim that’s still waiting, from `/v1/time-in-lieu?status=waiting`. They’re told.',
    params: [],
    fields: [
      { name: 'rate', says: '`1`, `1.5` or `2`. Leave it out for the company’s usual rate.' },
      { name: 'given_minutes', says: 'Instead of a rate: the exact time to give back, in minutes.' },
    ],
    example: '/v1/time-in-lieu/5c6d7e8f-9a0b-4c1d-8e2f-3a4b5c6d7e8f/approve',
    body: { rate: 1 },
    reply: { entry: { id: '5c6d7e8f-9a0b-4c1d-8e2f-3a4b5c6d7e8f', status: 'approved', minutes: 180, '…': '…' },
      message: 'Approved Sam Patel’s claim for 24/10/2026: 3h of time in lieu. They’ve been told.' },
  },
  {
    id: 'decline-time-in-lieu', method: 'POST', path: '/v1/time-in-lieu/{entry_id}/decline', tick: 'manage_allowances', title: 'Decline a time in lieu claim',
    what: 'A claim that’s still waiting. They’re told.',
    params: [],
    fields: [{ name: 'note', says: 'Optional, for them. Never sent back out.' }],
    example: '/v1/time-in-lieu/5c6d7e8f-9a0b-4c1d-8e2f-3a4b5c6d7e8f/decline',
    reply: { entry: { id: '5c6d7e8f-9a0b-4c1d-8e2f-3a4b5c6d7e8f', status: 'declined', minutes: null, '…': '…' },
      message: 'Declined Sam Patel’s claim for 24/10/2026. They’ve been told.' },
  },
  {
    id: 'correct-time-in-lieu', method: 'POST', path: '/v1/time-in-lieu/corrections', tick: 'manage_allowances', title: 'Correct time in lieu',
    what: 'Nothing is ever edited or deleted: a correction adds or takes away time, says why, and sits in their history beside what it corrects. It’s also how time in lieu from another system goes in. Time added follows the company’s “use within” rule from today, unless `no_expiry` is `true`.',
    params: [],
    fields: [
      { name: 'person_id', required: true, says: 'From `/v1/people`. Not an admin.' },
      { name: 'minutes', required: true, says: 'Plus to add, minus to take away, like `-90`.' },
      { name: 'reason', required: true, says: 'Why. Kept in their history and never sent back out.' },
      { name: 'no_expiry', says: '`true` if this time doesn’t expire. Normally `false`.' },
    ],
    example: '/v1/time-in-lieu/corrections',
    body: { person_id: SAM.id, minutes: -90, reason: 'Counted twice on 24/10' },
    reply: { entry: { id: '8f9a0b1c-2d3e-4f5a-9b6c-7d8e9f0a1b2c', person: SAM, kind: 'correction', status: 'approved', minutes: -90, '…': '…' },
      message: 'Corrected Sam Patel’s time in lieu by −1h 30m. It’s in their history, with what you wrote.' },
  },
  {
    id: 'add-leave-type', method: 'POST', path: '/v1/leave-types', tick: 'manage_allowances', title: 'Add a leave type',
    what: 'Never private and never sickness: make those in the app. Its payroll settings are the usual ones, marked for an admin to check in Settings → Leave types. No two types can share a name or a colour, and no type can be called Annual Leave.',
    params: [],
    fields: [
      { name: 'name', required: true, says: 'Like `Jury service`.' },
      { name: 'colour', says: 'From `/v1/leave-types/colours`, not in use. Leave it out for a free one.' },
      { name: 'icon', says: 'From `/v1/leave-types/icons`. Normally `calendar`.' },
      { name: 'counts_against_allowance', says: 'Normally `true`.' },
      { name: 'needs_approval', says: 'Normally `true`.' },
      { name: 'approve_automatically', says: 'When it breaks none of the company’s rules. Only with `needs_approval`.' },
      { name: 'counts_towards_max_off', says: 'Counts towards “how many can be off at once”. Normally `true`.' },
      { name: 'yearly_limit_days', says: 'The most anybody can have in a leave year. `null` for no limit.' },
    ],
    example: '/v1/leave-types',
    body: { name: 'Jury service', colour: '#3b82f6', icon: 'calendar', counts_against_allowance: false },
    reply: { leave_type: { id: '1b2c3d4e-5f6a-4b7c-8d9e-0f1a2b3c4d5e', name: 'Jury service', colour: '#3b82f6', icon: 'calendar',
      counts_against_allowance: false, needs_approval: true, approve_automatically: false, counts_towards_max_off: true,
      yearly_limit_days: null, is_annual_leave: false, is_time_in_lieu: false },
    message: 'Added the leave type Jury service. Its payroll settings are the usual ones and are marked for an admin to check in Settings → Leave types.' },
  },
  {
    id: 'edit-leave-type', method: 'PATCH', path: '/v1/leave-types/{type_id}', tick: 'manage_allowances', title: 'Change a leave type',
    what: 'Change only what you send; everything else about it stays as it is. A private or sickness type can’t be changed by a key. Annual Leave keeps its name and always counts against the allowance. Leave types can’t be deleted through the API.',
    params: [],
    fields: [{ name: 'name, colour, icon, counts_against_allowance, needs_approval, approve_automatically, counts_towards_max_off, yearly_limit_days', says: 'Any of them.' }],
    example: '/v1/leave-types/1b2c3d4e-5f6a-4b7c-8d9e-0f1a2b3c4d5e',
    body: { yearly_limit_days: 10 },
    reply: { leave_type: { id: '1b2c3d4e-5f6a-4b7c-8d9e-0f1a2b3c4d5e', name: 'Jury service', yearly_limit_days: 10, '…': '…' },
      message: 'Updated the leave type Jury service: yearly limit days.' },
  },
];

/* ── Webhooks (step 2, 25/10/2026) ────────────────────────────────────────────────────── */

/* The events, the box an admin ticks for each, and when it's sent. ⚠️ Must match
 * `services/webhooks.BOOKING_EVENTS` — `backend/tests/test_developer_page.py` checks. */
export const WEBHOOK_EVENTS = [
  { event: 'leave.requested', box: 'Requested', when: 'Somebody asks for leave that needs approving.' },
  { event: 'leave.approved', box: 'Approved (or booked already approved)', when: 'The final approval — or a booking that was approved as it was made (an admin booking it, a type that needs no approval, approved automatically by the company’s rules). Then there’s no `leave.requested` first.' },
  { event: 'leave.declined', box: 'Declined', when: 'A request is declined, at any approval step. The decline note is never sent.' },
  { event: 'leave.cancelled', box: 'Cancelled, or some days given back', when: 'A booking is cancelled — by the person, an admin, or because they left. Giving back every day of a booking is a cancel.' },
  { event: 'leave.days_given_back', box: 'Cancelled, or some days given back', when: 'Some of a booking’s days are given back. `booking` is how it now stands, `given_back` lists the dates, and if a day in the middle was given back, `other_parts` holds the part that split off.' },
  { event: 'test', box: '(Send a test)', when: 'An admin pressed Send a test in Settings → Integrations. No booking.' },
];

/* A made-up message. ⚠️ No reason — ever. `test_developer_page.py` reads this. */
export const WEBHOOK_EXAMPLE = {
  event: 'leave.approved',
  event_id: '2b0f6c1e-8a4d-4f8e-b1f2-0c9d5e7a3b61',
  sent_at: '2026-10-25T14:02:11Z',
  occurred_at: '2026-10-25T14:02:10Z',
  company: { id: '0f3e…', name: 'Acme Ltd' },
  by: { id: 'b7c8…', name: 'Dana Rivers', via_api_key: null },
  booking: {
    id: '7e8c4d0d-1dac-4826-a740-87cf92d3cd89', person: JO_IN_BOOKING, leave_type: ANNUAL,
    starts: '2026-11-02', ends: '2026-11-06', start_part: 'full_day', end_part: 'full_day',
    start_time: null, end_time: null, amount: { unit: 'days', value: 5, text: '5 days' },
    status: 'approved', booked_at: '2026-10-20T09:14:02Z', approved_at: '2026-10-25T14:02:10Z',
  },
};

export const WEBHOOK_HEADERS = [
  ['Content-Type', 'application/json'],
  ['HolidayHive-Signature', 't=1792915331,v1=5257a869e7ecebeda32affa62cdca3fa51cad7e77a0e56ff536d0ce8e108d8bd'],
  ['HolidayHive-Event-Id', '2b0f6c1e-8a4d-4f8e-b1f2-0c9d5e7a3b61'],
];

/* The receiver's check, in the page's two languages. The same recipe as
 * `services/webhook_signing.verify` — HMAC-SHA256 of "{t}.{raw body}" with the address's secret,
 * and a 5-minute window. ⚠️ PowerShell: no `&&`, no `<placeholders>` (WORKING-WITH-ROB.md). */
export const SIGNATURE_CHECK = {
  python: [
    'import hashlib, hmac, time',
    '',
    'def is_from_holidayhive(secret: str, header: str, raw_body: bytes) -> bool:',
    '    """`header` is the HolidayHive-Signature header; `raw_body` the bytes exactly as they arrived."""',
    '    parts = dict(p.split("=", 1) for p in header.split(",") if "=" in p)',
    '    t, sent = parts.get("t", ""), parts.get("v1", "")',
    '    if not t.isdigit() or abs(time.time() - int(t)) > 300:',
    '        return False          # missing, or more than 5 minutes old',
    '    expected = hmac.new(secret.encode(), f"{t}.".encode() + raw_body, hashlib.sha256).hexdigest()',
    '    return hmac.compare_digest(expected, sent)',
  ].join('\n'),
  powershell: [
    'function Test-HolidayHiveSignature {',
    '    param([string]$Secret, [string]$Header, [byte[]]$RawBody)',
    '    $parts = @{}',
    "    foreach ($p in $Header.Split(',')) { $k, $v = $p.Split('=', 2); $parts[$k.Trim()] = $v }",
    "    $t = [long]$parts['t']",
    '    $now = [DateTimeOffset]::UtcNow.ToUnixTimeSeconds()',
    '    if ([Math]::Abs($now - $t) -gt 300) { return $false }   # missing, or more than 5 minutes old',
    '    $hmac = [System.Security.Cryptography.HMACSHA256]::new([Text.Encoding]::UTF8.GetBytes($Secret))',
    '    $signed = [byte[]]([Text.Encoding]::UTF8.GetBytes("$t.") + $RawBody)',
    "    $expected = -join ($hmac.ComputeHash($signed) | ForEach-Object { $_.ToString('x2') })",
    "    return $expected -ceq $parts['v1']",
    '}',
  ].join('\n'),
};

export const ERRORS = [
  ['400', 'missing, bad_date, bad_range, bad_id, bad_limit, bad_cursor, bad_flag, bad_status', 'Something in the address or the body isn’t right. The message says what.'],
  ['400', 'bad_json, unknown_field, bad_part, bad_time, bad_text, bad_idempotency_key', 'The body of a change isn’t right — for example a field we don’t know (a typo is refused, not ignored).'],
  ['400', 'bad_email, bad_unit, bad_number, bad_contacts, too_many, cant_clear, nothing_to_change', 'Something about a person or department you sent isn’t right. The message says what.'],
  ['400', 'refused', 'HolidayHive said no, just as the app would: not enough allowance, already booked off, a no-leave date. The message is the app’s own sentence.'],
  ['400', 'type_not_bookable', 'That leave type shows as `Time off` to this key, so it can’t book it.'],
  ['400', 'bad_rate, rate_or_minutes, bad_colour, bad_icon, needs_approval_first, time_in_lieu_own_balance, bad_kind', 'Something about time in lieu or a leave type you sent isn’t right. The message says what.'],
  ['401', 'no_key', 'No key was sent.'],
  ['401', 'key_not_valid', 'The key isn’t one we know, or it was revoked, or the admin who made it is no longer an admin.'],
  ['403', 'not_ticked', 'The key works, but it wasn’t ticked for this.'],
  ['403', 'not_allowed', 'The admin who made the key isn’t allowed to do that in the app either.'],
  ['403', 'admins_off_limits', 'A key can’t change, archive or restore an admin, or change their allowance or time in lieu. Do that in HolidayHive.'],
  ['403', 'type_off_limits, type_setting_off_limits', 'A key can’t change a private or sickness leave type, or make one private or sickness, or change payroll settings.'],
  ['403', 'annual_leave_locked', 'Annual Leave keeps its name and always counts against the allowance, and no other type can be called Annual Leave.'],
  ['404', 'not_found', 'There’s nothing with that id in your company.'],
  ['409', 'already_decided', 'That request has already been approved, declined or cancelled.'],
  ['409', 'already_one_of_yours', 'That email belongs to one of your people already. The message gives their id.'],
  ['409', 'email_registered', 'That email is already registered on HolidayHive by somebody outside your company.'],
  ['409', 'not_leaving', 'Restore was asked for somebody who isn’t archived or leaving.'],
  ['409', 'archived', 'That person is archived. Restore them first.'],
  ['409', 'name_taken, colour_taken', 'Another leave type already has that name or colour.'],
  ['409', 'idempotency_key_reused, in_progress', 'That `Idempotency-Key` went with a different request, or the first one is still being worked on.'],
  ['429', 'too_many_requests', 'More than 5 requests in a second. Wait for the number of seconds in `Retry-After`.'],
  ['429', 'too_many_registered_emails', 'Your keys tried 10 emails already registered elsewhere in 24 hours, so adding and changing people is paused until the time in the message. Look people up first.'],
  ['500', 'something_went_wrong', 'Our fault. Check whether the change was made before trying again.'],
];

/* "Retrying safely": one booking sent with a label the program makes up once and keeps for its
 * retries. Pure. A new GUID per booking is the safe habit, so that's what the example does. */
export function retryExample(lang, base) {
  const url = fullUrl(base, '/v1/bookings');
  if (lang === 'powershell') {
    return [
      '$key = "hh_live_PASTE-YOUR-KEY-HERE"',
      '$label = [guid]::NewGuid().ToString()   # once per booking; the same value for its retries',
      `$body = '${JSON.stringify({ person_id: '6d7a1b71-9beb-4f5b-994a-19467fbbc927', leave_type_id: '3d18d31d-383f-48db-8d2d-01ac66d7a695', starts: '2026-11-02' })}'`,
      `Invoke-RestMethod -Method Post -Uri "${url}" -Headers @{ Authorization = "Bearer $key"; "Idempotency-Key" = $label } -ContentType "application/json" -Body $body`,
    ].join('\n');
  }
  return [
    'LABEL=$(uuidgen)   # once per booking; the same value for its retries',
    [`curl -X POST "${url}"`,
     '  -H "Authorization: Bearer $HH_KEY"',
     '  -H "Idempotency-Key: $LABEL"',
     '  -H "Content-Type: application/json"',
     `  -d '${JSON.stringify({ person_id: '6d7a1b71-9beb-4f5b-994a-19467fbbc927', leave_type_id: '3d18d31d-383f-48db-8d2d-01ac66d7a695', starts: '2026-11-02' })}'`].join(' \\\n'),
  ].join('\n');
}

/* The address with its base, ready to paste. Pure. */
export function fullUrl(base, example) {
  return `${(base || '').replace(/\/+$/, '')}${example}`;
}

/* The request in the language chosen. Pure. ⚠️ PowerShell: no `&&`, and the key line is
 * obviously not runnable until the key is pasted in (WORKING-WITH-ROB.md rule 2).
 * A change (`method` POST, PATCH or DELETE) sends JSON — `body`, or nothing.
 * ⚠️ No Idempotency-Key in these: a label copied from an example and reused for a different
 * booking is refused (409). "Retrying safely" shows it on its own, with a fresh value each time. */
export function requestFor(lang, base, example, method = 'GET', body = null) {
  const url = fullUrl(base, example);
  const post = method !== 'GET';
  const json = body ? JSON.stringify(body) : null;
  /* PowerShell's -Method takes Post, Put, Patch, Delete. */
  const psMethod = method.charAt(0) + method.slice(1).toLowerCase();
  if (lang === 'powershell') {
    if (!post) {
      return [
        '$key = "hh_live_PASTE-YOUR-KEY-HERE"',
        `Invoke-RestMethod -Uri "${url}" -Headers @{ Authorization = "Bearer $key" } | ConvertTo-Json -Depth 8`,
      ].join('\n');
    }
    return [
      '$key = "hh_live_PASTE-YOUR-KEY-HERE"',
      ...(json ? [`$body = '${json}'`] : []),
      `Invoke-RestMethod -Method ${psMethod} -Uri "${url}" -Headers @{ Authorization = "Bearer $key" }`
        + (json ? ' -ContentType "application/json" -Body $body' : '') + ' | ConvertTo-Json -Depth 8',
    ].join('\n');
  }
  if (!post) return `curl "${url}" \\\n  -H "Authorization: Bearer $HH_KEY"`;
  return [
    `curl -X ${method} "${url}"`,
    '  -H "Authorization: Bearer $HH_KEY"',
    ...(json ? ['  -H "Content-Type: application/json"', `  -d '${json}'`] : []),
  ].join(' \\\n');
}
